import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Incident } from '@/hooks/queries/useIncidents';

interface IncidentEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incident?: Incident;
  onSave: (id: string, data: any) => void;
}

export function IncidentEditDialog({ open, onOpenChange, incident, onSave }: IncidentEditDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    priority: 'medium',
    impact: '',
    status: 'draft',
    actions: '',
    early_report: '',
    initial_72h_report: '',
    final_30d_report: '',
    lessons_learned: '',
  });

  useEffect(() => {
    if (incident) {
      setFormData({
        title: incident.title,
        priority: incident.priority,
        impact: incident.impact || '',
        status: incident.status,
        actions: incident.actions || '',
        early_report: incident.early_report || '',
        initial_72h_report: incident.initial_72h_report || '',
        final_30d_report: incident.final_30d_report || '',
        lessons_learned: incident.lessons_learned || '',
      });
    }
  }, [incident]);

  const handleSave = () => {
    if (incident?.id) {
      onSave(incident.id, formData);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Incident</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="reported">Reported</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="impact">Impact</Label>
            <Textarea
              id="impact"
              value={formData.impact}
              onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="actions">Actions Taken</Label>
            <Textarea
              id="actions"
              value={formData.actions}
              onChange={(e) => setFormData({ ...formData, actions: e.target.value })}
              rows={3}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="early_report">Early Warning Report</Label>
            <Textarea
              id="early_report"
              value={formData.early_report}
              onChange={(e) => setFormData({ ...formData, early_report: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="initial_72h_report">Initial 72h Report</Label>
            <Textarea
              id="initial_72h_report"
              value={formData.initial_72h_report}
              onChange={(e) => setFormData({ ...formData, initial_72h_report: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="final_30d_report">Final 30-Day Report</Label>
            <Textarea
              id="final_30d_report"
              value={formData.final_30d_report}
              onChange={(e) => setFormData({ ...formData, final_30d_report: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="lessons_learned">Lessons Learned</Label>
            <Textarea
              id="lessons_learned"
              value={formData.lessons_learned}
              onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!formData.title}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
